import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common/Panel";
import { AIMarkdown } from "@/components/common/AIMarkdown";
import { AIChat } from "@/components/common/AIChat";
import { CaseIntelligence, TreatmentAlerts } from "@/components/matter/CaseIntelligence";
import { HearingPrepCard } from "@/components/matter/HearingPrepCard";
import { MODEL_LABEL } from "@/lib/sse";
import { enqueueJob } from "@/pages/Jobs";
import { fromNow } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Sparkles, RefreshCw, ShieldQuestion, MessageSquare } from "lucide-react";
import { AnimationFrame } from "@/components/common/AnimationFrame";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AISimPanel({ matter }) {
  const { create, scope } = useApp();
  const briefAction = (kind, text) => {
    const clean = text.replace(/\s*\((High|Medium|Low)\)\s*$/i, "").slice(0, 200);
    if (kind === "task") { create("tasks", { title: clean, matterId: matter.id, assignee: scope.dirId, priority: "Medium", status: "To Do", dueDate: null, category: "AI Brief", description: `From Matter Intelligence brief: ${text}` }); toast.success("Task created from brief."); }
    else { create("notes", { matterId: matter.id, type: "Strategy Note", body: clean, pinned: true, date: new Date().toISOString() }); toast.success("Pinned to Strategy notes."); }
  };
  const [status, setStatus] = useState("loading"); // loading | idle | running | done | error
  const [text, setText] = useState("");
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("brief");
  const [stage, setStage] = useState("");

  useEffect(() => {
    setStatus("loading");
    axios.get(`${API}/ai/matter/${matter.id}/analysis`).then((r) => {
      if (r.data) { setText(r.data.content); setMeta({ model: r.data.model, createdAt: r.data.createdAt }); setStatus("done"); }
      else setStatus("idle");
    }).catch(() => setStatus("idle"));
  }, [matter.id]);

  const run = async () => {
    setStatus("running"); setText(""); setError("");
    try {
      const job = await enqueueJob("ai_analysis", { matterId: matter.id });
      setStage(job.progress?.stage || "Queued");
      const poll = setInterval(async () => {
        try {
          const { data } = await axios.get(`${API}/jobs/${job.id}`);
          setStage(data.progress?.stage || data.status);
          if (data.status === "COMPLETED") {
            clearInterval(poll);
            const r = await axios.get(`${API}/ai/matter/${matter.id}/analysis`);
            setText(r.data.content); setMeta({ model: r.data.model, createdAt: r.data.createdAt }); setStatus("done");
          } else if (data.status === "FAILED" || data.status === "CANCELLED") {
            clearInterval(poll); setError(data.error || "Analysis did not finish. Retry from Background Jobs."); setStatus("error");
          }
        } catch (e) { /* keep polling */ }
      }, 2500);
    } catch (e) { setError(e?.response?.data?.detail || "Could not queue analysis."); setStatus("error"); }
  };

  if (status === "loading") return <Panel className="p-12 text-center text-sm text-ink-muted">Loading intelligence…</Panel>;

  if (status === "idle" || status === "error") {
    return (
      <div className="space-y-3">
      <TreatmentAlerts matterId={matter.id} />
      <Panel className="flex flex-col items-center justify-center p-12 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-copper-light"><Sparkles className="h-7 w-7 text-copper" /></div>
        <h3 className="mt-4 font-display text-2xl font-semibold text-ink">Matter Intelligence</h3>
        <p className="mt-1 max-w-md text-sm text-ink-2">Claude reads every hearing, task, document and note on this matter and returns a structured brief — every point traceable to a source.</p>
        {error && <div className="mt-3 rounded-lg bg-danger/10 px-3 py-2 text-xs text-danger" data-testid="ai-error">{error}</div>}
        <Button className="mt-5" onClick={run} data-testid="run-ai-btn"><Sparkles className="mr-2 h-4 w-4" /> Analyse Matter</Button>
      </Panel>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1 rounded-lg border border-line bg-surface p-0.5">
          {[["brief", "Intelligence Brief", Sparkles], ["chat", "Ask about this matter", MessageSquare]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={cn("flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium", tab === k ? "bg-navy text-white" : "text-ink-2 hover:text-ink")} data-testid={`ai-tab-${k}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-muted" data-testid="ai-meta">
          {meta?.model && <span className="rounded-full bg-copper-light px-2 py-0.5 font-medium text-copper">{MODEL_LABEL[meta.model] || meta.model}</span>}
          {status === "running" ? <span className="animate-pulse text-copper" data-testid="ai-job-stage">{stage || "Analysing…"}</span> : meta?.createdAt && <span>Generated {fromNow(meta.createdAt)}</span>}
          <Button size="sm" variant="outline" onClick={run} disabled={status === "running"} data-testid="rerun-ai-btn"><RefreshCw className={cn("mr-1 h-3.5 w-3.5", status === "running" && "animate-spin")} /> Re-analyse</Button>
        </div>
      </div>

      <CaseIntelligence matterId={matter.id} />
      <HearingPrepCard matterId={matter.id} />
      {tab === "brief" ? (
        <Panel className="p-5" data-testid="ai-brief">
          {status === "running" && <AnimationFrame slug="agent-orbit" title="Agents coordinating the matter" height={190} maxWidth={320} className="mb-3" />}
          <AIMarkdown text={text || (status === "running" ? `Background job running — ${stage || "queued"}. You can leave this tab; the brief is saved when done.` : "…")} onAction={status === "running" ? undefined : briefAction} />
          {status === "running" && <span className="inline-block h-4 w-1.5 animate-pulse bg-copper align-middle" />}
        </Panel>
      ) : (
        <Panel className="h-[560px] p-4">
          <AIChat context={{ mode: "matter", matterId: matter.id }} placeholder="e.g. What should I press for at the next hearing?"
            suggestions={["Summarise the strongest ground in two lines", "What is missing from the record before the next hearing?", "Draft 5 cross-examination questions for the opponent's key witness"]} />
        </Panel>
      )}
      <div className="flex items-center gap-2 rounded-lg bg-warning-bg px-3 py-2 text-xs text-warning">
        <ShieldQuestion className="h-4 w-4" /> AI output is indicative and requires review — never treat as legally conclusive.
      </div>
    </div>
  );
}
